import { useState } from 'react'
import { useApi } from '../../../../api/api'
import { useSelector } from 'react-redux'
import datasourceDefs from '../../../../api/datasourceConfig'
import {
    ScoopTextField,
    ScoopButton,
    ScoopDialog,
    ScoopLinearProgress,
    ScoopText,
    useToast,
} from '../../../../components/ui'
import styles from './Apollo.module.scss'

const bot = datasourceDefs.find((b) => b.name === 'Apollo')

export const Apollo = ({ onClose, stepBack, type, basicConfiguration }) => {
    const toast = useToast()
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const { postData } = useApi(bot.API_URL)
    const [url, setUrl] = useState('https://app.apollo.io/#/control-center/')
    const [isUrlValid, setIsUrlValid] = useState(true)
    const [reportName, setReportName] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [nextStep, setNextStep] = useState(false)
    const [isConnetionTested, setIsConnetionTested] = useState(false)
    const [botUID, setBotUID] = useState('')
    const [botPWD, setBotPWD] = useState('')

    const handleUrlChange = (e) => {
        const newUrl = e.target.value
        setUrl(newUrl)
        // Validate URL
        setIsUrlValid(bot.urlValidation.test(newUrl))
    }

    const runBot = async () => {
        const action = {
            action: 'testRobot',
            service: bot.service,
            reportURL: url,
            botUID: botUID,
            botPWD: botPWD,
            label: 'Apollo TEST',
            inboxName: 'Apollo_TEST',
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }

        const result = await postData(action)
        return result
    }

    const handleRunBot = async () => {
        setIsLoading(true)
        try {
            const data = await runBot()
            if (data?.status === 'error') {
                toast.error(
                    'Failed to fetch data from Apollo. Please check the credentials you provided.'
                )
            } else {
                toast.success('Apollo connector is connected successfully')
                setIsConnetionTested(true)
            }
        } catch (error) {
            console.log(error)
        }
        setIsLoading(false)
    }

    function createInboxName(userID, reportName) {
        // Combine userID and reportName with an underscore
        let combinedName = `${userID}_${reportName.substring(0, 30)}`

        // Replace characters not in the allowed list with an underscore
        // Allowed characters: alphanumeric, plus (+), minus (-), equals (=), dot (.), comma (,), underscore (_), colon (:), at (@)
        let scrubbedName = combinedName.replace(/[^a-zA-Z0-9\+\-\=\.\,\_\:\@]/g, '_')

        return scrubbedName
    }

    const createBot = async () => {
        const action = {
            action: 'createRobot',
            inboxName: createInboxName(userID, reportName),
            keepOnlyCurrent: basicConfiguration?.isMostRecent,
            incremental: basicConfiguration?.isIncremental,
            enableMultipleSameDayLoads: basicConfiguration?.isMultipleLoads,
            label: reportName,
            workspaceID: workspaceID,
            reportURL: url,
            isTransactional: type === 'Transactional',
            userName: userID,
            service: bot.service,
            botUID: botUID,
            botPWD: botPWD,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }

        const result = await postData(action)
        return result
    }

    const handleCreateBot = () => {
        createBot().then((results) => {
            console.log(results)
            if (results?.status === 'success')
                toast.success('Apollo app connector created successfully')
            else if (results?.status === 'error')
                toast.error(results?.message ?? 'Failed to create Apollo app connector')
            setNextStep(false)
            stepBack()
            onClose()
        })
    }

    return (
        <>
            {!nextStep ? (
                <ScoopDialog
                    open
                    title={'New Report from Apollo'}
                    icon={bot.icon}
                    onClose={onClose}
                    maxWidth="lg"
                    actions={
                        <>
                            <ScoopButton variant="text" size="sm" onClick={stepBack}>
                                Back
                            </ScoopButton>
                            {url && botUID && botPWD && (
                                <ScoopButton
                                    size="sm"
                                    onClick={() => setNextStep(true)}
                                    disabled={!isConnetionTested}
                                >
                                    {' '}
                                    I've done this{' '}
                                </ScoopButton>
                            )}
                        </>
                    }
                >
                    <div className={styles.dialogContent}>
                        <div className={styles.stepper}>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>1</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Enter the User Name and Password for the Apollo Account you
                                        want to connect to
                                    </ScoopText>
                                </div>
                                <div className={styles.stepContent}>
                                    <div className={styles.gridRow}>
                                        <ScoopTextField
                                            label="User Name"
                                            value={botUID}
                                            onChange={(event) => setBotUID(event.target.value)}
                                        />
                                        <ScoopTextField
                                            label="User Password"
                                            type="password"
                                            value={botPWD}
                                            onChange={(event) => setBotPWD(event.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>2</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Paste the entire report URL below
                                    </ScoopText>
                                </div>
                                <div className={styles.stepContent}>
                                    <ScoopTextField
                                        label="Apollo Reports URL"
                                        value={url}
                                        onChange={handleUrlChange}
                                    />
                                    {!isUrlValid && (
                                        <ScoopText className={styles.errorText}>
                                            Invalid Apollo Reports URL
                                        </ScoopText>
                                    )}
                                </div>
                            </div>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>3</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Run Connectivity Test
                                    </ScoopText>
                                </div>
                                <div className={styles.stepContent}>
                                    {url && botUID && botPWD && (
                                        <ScoopButton
                                            size="sm"
                                            fullWidth
                                            onClick={handleRunBot}
                                            className={styles.testButton}
                                        >
                                            Run a Test
                                        </ScoopButton>
                                    )}
                                </div>
                            </div>

                            {isLoading && (
                                <ScoopLinearProgress
                                    variant="indeterminate"
                                    className={styles.testButton}
                                />
                            )}
                        </div>
                    </div>
                </ScoopDialog>
            ) : (
                <ScoopDialog
                    open
                    title={'New Report from Apollo'}
                    icon={bot.icon}
                    onClose={onClose}
                    actions={
                        <>
                            <ScoopButton
                                variant="text"
                                size="sm"
                                onClick={() => setNextStep(false)}
                            >
                                Back
                            </ScoopButton>
                            <ScoopButton size="sm" onClick={() => handleCreateBot()}>
                                {' '}
                                Create Dataset{' '}
                            </ScoopButton>
                        </>
                    }
                    className={styles.dialogNarrow}
                >
                    <div className={styles.infoBox}>
                        <ScoopText>
                            Data will be Scooped from <b>{reportName}</b>
                        </ScoopText>
                    </div>
                    <ScoopTextField
                        label={'Dataset name'}
                        onChange={(event) => {
                            setReportName(event.target.value)
                        }}
                        value={reportName}
                    />
                </ScoopDialog>
            )}
        </>
    )
}
